<template>
  <div class="container py-4">
    <h2 class="mb-3">Bulk Question Creator</h2>

    <div class="card p-3 mb-3">
      <div class="row g-3">
        <div class="col-md-3">
          <label class="form-label">Grade</label>
          <select v-model="selectedGrade" class="form-select" @change="onGradeChange">
            <option value="" disabled>Select grade</option>
            <option v-for="g in gradeLevels" :key="g.id" :value="g.id">{{ g.grade_name }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Subject</label>
          <select v-model="selectedSubject" class="form-select" :disabled="!subjects.length" @change="onSubjectChange">
            <option value="" disabled>Select subject</option>
            <option v-for="s in subjects" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div class="col-md-5">
          <label class="form-label">Topic</label>
          <select v-model="selectedTopic" class="form-select" :disabled="!topics.length">
            <option value="" disabled>Select topic</option>
            <option v-for="t in topics" :key="t.id" :value="t.id">{{ t.topic_name }}</option>
          </select>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-between align-items-center mb-2">
      <h5 class="mb-0">Questions ({{ items.length }})</h5>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm" @click="addItem(5)">+5</button>
        <button class="btn btn-outline-secondary btn-sm" @click="addItem(1)">+1</button>
      </div>
    </div>

    <div v-if="items.length === 0" class="alert alert-info">Use +1 to add a question row.</div>

    <div v-for="(it, idx) in items" :key="it.uid" class="card mb-3 shadow-sm">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div class="fw-semibold">#{{ idx + 1 }}</div>
          <div class="d-flex align-items-center gap-2">
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" v-model="it.useTextarea" :id="`ta_${it.uid}`">
              <label class="form-check-label" :for="`ta_${it.uid}`">Long Text</label>
            </div>

            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" v-model="it.is_math" :id="`math_${it.uid}`">
              <label class="form-check-label" :for="`math_${it.uid}`">Math</label>
            </div>

            <button class="btn btn-sm btn-outline-danger" @click="removeItem(idx)">&times;</button>
          </div>
        </div>

        <div class="row g-3">
          <div class="col-md-3" v-if="!it.hasSubQuestions">
            <label class="form-label">Type</label>
            <select v-model="it.question_type" class="form-select form-select-sm" @change="onRowTypeChange(it)">
              <option value="mcq">Multiple Choice</option>
              <option value="true_false">True / False</option>
              <option value="short_answer">Short Answer</option>
              <option value="matching">Matching</option>
            </select>

            <!-- MCQ multi-answer toggle -->
            <div v-if="it.question_type === 'mcq'" class="form-check form-switch mt-2">
              <input class="form-check-input" type="checkbox" v-model="it.multiple_answers" :id="`multi_${it.uid}`" />
              <label class="form-check-label" :for="`multi_${it.uid}`">Multiple correct answers</label>
            </div>
          </div>

          <div class="col-md-9">
            <label class="form-label">Question</label>

            <template v-if="it.is_math">
              <textarea
                :ref="`qta_${it.uid}`"
                class="form-control form-control-sm mb-1"
                v-model="it.question"
                :rows="it.useTextarea ? 4 : 2"
                placeholder="Enter question..."
              />
              <math-field :ref="`qmf_${it.uid}`" virtual-keyboard-mode="onfocus" class="w-100 border rounded p-2" style="min-height: 40px"></math-field>
              <div class="mt-1 d-flex gap-2">
                <button type="button" class="btn btn-outline-primary btn-sm" @click="insertRowMathAtCursor(it, 'question', 'inline')">Inline ($...$)</button>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="insertRowMathAtCursor(it, 'question', 'block')">Block ($$...$$)</button>
              </div>
            </template>

            <template v-else>
              <textarea v-if="it.useTextarea" class="form-control form-control-sm" v-model="it.question" rows="4" placeholder="Enter question..."></textarea>
              <input v-else class="form-control form-control-sm" v-model="it.question" placeholder="Enter question..." />
            </template>

            <div class="mt-2 d-flex align-items-center gap-2">
              <label class="btn btn-outline-secondary btn-sm mb-0">
                <input type="file" accept="image/*" :id="`qi_${it.uid}`" style="display:none" @change="onQuestionImageChange(it, $event)" />
                Add image
              </label>
              <button v-if="it.questionImagePreview" type="button" class="btn btn-outline-danger btn-sm" @click="removeQuestionImage(it)">&times; Remove</button>
            </div>
            <div v-if="it.questionImagePreview" class="mt-2">
              <img :src="it.questionImagePreview" alt="Question Image Preview" class="img-thumbnail" style="max-width: 200px; max-height: 150px" />
            </div>
          </div>

          <!-- MCQ -->
          <template v-if="it.question_type === 'mcq' && !it.hasSubQuestions">
            <div class="col-12">
              <label class="form-label">Options</label>

              <div class="d-flex flex-wrap gap-2">
                <div
                  v-for="(opt, oidx) in it.options"
                  :key="oidx"
                  class="input-group input-group-sm align-items-start"
                  style="max-width: 520px;"
                >
                  <div class="input-group-text">
                    <!-- single answer => radio, multi => checkbox -->
                    <input
                      v-if="!it.multiple_answers"
                      type="radio"
                      :name="`ca_${it.uid}`"
                      :value="opt"
                      :checked="(it.correct_answer?.[0] ?? '') === opt"
                      @change="setRowCorrectSingle(it, opt)"
                    />
                    <input
                      v-else
                      type="checkbox"
                      :value="opt"
                      :checked="Array.isArray(it.correct_answer) && it.correct_answer.includes(opt)"
                      @change="toggleRowCorrectMulti(it, opt)"
                    />
                  </div>

                  <template v-if="it.is_math">
                    <textarea
                      :ref="`ota_${it.uid}_${oidx}`"
                      class="form-control"
                      v-model="it.options[oidx]"
                      :placeholder="`Option ${String.fromCharCode(65 + oidx)}`"
                      :rows="it.useTextarea ? 3 : 2"
                      @paste="onOptionsPaste(it, oidx, $event)"
                    ></textarea>
                    <button class="btn btn-outline-primary" type="button" @click="insertRowMathAtCursor(it, oidx, 'inline')">Inline</button>
                    <button class="btn btn-outline-secondary" type="button" @click="insertRowMathAtCursor(it, oidx, 'block')">Block</button>
                  </template>

                  <template v-else>
                    <input
                      class="form-control"
                      v-model="it.options[oidx]"
                      :placeholder="`Option ${String.fromCharCode(65 + oidx)}`"
                      @paste="onOptionsPaste(it, oidx, $event)"
                    />
                  </template>

                  <label class="btn btn-outline-secondary ms-1" title="Add image" style="font-size: 0.75rem;">
                    <input type="file" accept="image/*" style="display:none" @change="onOptionImageChange(it, oidx, $event)" />
                    Img
                  </label>

                  <div v-if="it.optionImagePreviews && it.optionImagePreviews[oidx]" class="ms-1 d-flex flex-column align-items-start">
                    <img :src="it.optionImagePreviews[oidx]" alt="Option Image Preview" class="img-thumbnail mb-1" style="max-width: 72px; max-height: 48px; object-fit: contain;" />
                    <button type="button" class="btn btn-outline-danger btn-sm" @click="clearOptionImage(it, oidx)">&times;</button>
                  </div>

                  <button
                    class="btn btn-outline-danger ms-1"
                    v-if="it.options.length > 2"
                    @click="removeRowOption(it, oidx)"
                    type="button"
                  >
                    &times;
                  </button>
                </div>
              </div>

              <div class="mt-2 d-flex gap-2">
                <button class="btn btn-outline-success btn-sm" type="button" @click="addRowOption(it)">Add option</button>
              </div>
            </div>
          </template>

          <!-- True/False -->
          <template v-else-if="it.question_type === 'true_false' && !it.hasSubQuestions">
            <div class="col-12">
              <label class="form-label d-block">Correct answer</label>

              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  :name="`tf_${it.uid}`"
                  value="true"
                  :checked="(it.correct_answer?.[0] ?? '') === 'true'"
                  @change="setRowCorrectSingle(it, 'true')"
                />
                <label class="form-check-label">True</label>
              </div>

              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  :name="`tf_${it.uid}`"
                  value="false"
                  :checked="(it.correct_answer?.[0] ?? '') === 'false'"
                  @change="setRowCorrectSingle(it, 'false')"
                />
                <label class="form-check-label">False</label>
              </div>
            </div>
          </template>

          <!-- Short Answer -->
          <template v-else-if="it.question_type === 'short_answer' && !it.hasSubQuestions">
            <div class="col-12">
              <label class="form-label">Expected Answer</label>

              <template v-if="it.is_math">
                <textarea :ref="`ata_${it.uid}`" class="form-control form-control-sm mb-1" v-model="it.short_answer_text" :rows="it.useTextarea ? 3 : 2" placeholder="Enter expected answer..."></textarea>
                <math-field :ref="`amf_${it.uid}`" virtual-keyboard-mode="onfocus" class="w-100 border rounded p-2" style="min-height: 36px"></math-field>
                <div class="mt-1 d-flex gap-2">
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="insertRowMathAtCursor(it, 'answer', 'inline')">Inline ($...$)</button>
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="insertRowMathAtCursor(it, 'answer', 'block')">Block ($$...$$)</button>
                </div>
              </template>

              <template v-else>
                <textarea v-if="it.useTextarea" class="form-control form-control-sm" v-model="it.short_answer_text" rows="3" placeholder="Enter expected answer..."></textarea>
                <input v-else class="form-control form-control-sm" v-model="it.short_answer_text" placeholder="Enter expected answer..." />
              </template>

              <div class="mt-2 d-flex align-items-center gap-2">
                <label class="btn btn-success btn-sm mb-0">
                  <input type="file" accept="image/*" :id="`cai_${it.uid}`" style="display:none" @change="onCorrectAnswerImageChange(it, $event)" />
                  Add answer image
                </label>
                <button v-if="it.correctAnswerImagePreview" type="button" class="btn btn-outline-danger btn-sm" @click="removeCorrectAnswerImage(it)">
                  &times; Remove
                </button>
              </div>

              <div v-if="it.correctAnswerImagePreview" class="mt-2">
                <img :src="it.correctAnswerImagePreview" alt="Correct Answer Image Preview" class="img-thumbnail" style="max-width: 200px; max-height: 150px" />
              </div>
            </div>
          </template>

          <!-- Matching -->
          <template v-else-if="it.question_type === 'matching' && !it.hasSubQuestions">
            <div class="col-md-6">
              <strong>Left Column</strong>
              <div v-for="(lv, li) in it.matching_items.left" :key="'l-' + li" class="input-group input-group-sm mb-2">
                <input class="form-control" v-model="it.matching_items.left[li]" :placeholder="`Left ${li+1}`" />
                <button class="btn btn-outline-danger" v-if="it.matching_items.left.length>2" type="button" @click="removeMatchingItem(it,'left', li)">&times;</button>
              </div>
              <button class="btn btn-outline-success btn-sm" type="button" @click="addMatchingItem(it, 'left')">+ Add Left</button>
            </div>

            <div class="col-md-6">
              <strong>Right Column</strong>
              <div v-for="(rv, ri) in it.matching_items.right" :key="'r-' + ri" class="input-group input-group-sm mb-2">
                <input class="form-control" v-model="it.matching_items.right[ri]" :placeholder="`Right ${ri+1}`" />
                <button class="btn btn-outline-danger" v-if="it.matching_items.right.length>2" type="button" @click="removeMatchingItem(it,'right', ri)">&times;</button>
              </div>
              <button class="btn btn-outline-success btn-sm" type="button" @click="addMatchingItem(it, 'right')">+ Add Right</button>
            </div>

            <div class="col-12 mt-2">
              <label class="form-label">Matching Pairs</label>

              <div v-for="(pair, pi) in it.matching_pairs" :key="'p-' + pi" class="row g-2 align-items-center mb-2">
                <div class="col-5">
                  <select v-model.number="pair.left_index" class="form-select form-select-sm">
                    <option :value="null" disabled>Select Left</option>
                    <option v-for="(lv, li) in it.matching_items.left" :key="'lopt-' + li" :value="li">{{ lv || `Left ${li+1}` }}</option>
                  </select>
                </div>
                <div class="col-2 text-center">→</div>
                <div class="col-5">
                  <select v-model.number="pair.right_index" class="form-select form-select-sm">
                    <option :value="null" disabled>Select Right</option>
                    <option v-for="(rv, ri) in it.matching_items.right" :key="'ropt-' + ri" :value="ri">{{ rv || `Right ${ri+1}` }}</option>
                  </select>
                </div>
                <div class="col-12">
                  <button v-if="it.matching_pairs.length>1" class="btn btn-outline-danger btn-sm" type="button" @click="removeMatchingPair(it, pi)">&times; Remove Pair</button>
                </div>
              </div>

              <button class="btn btn-outline-success btn-sm" type="button" @click="addMatchingPair(it)">+ Add Pair</button>
            </div>
          </template>

          <!-- Sub-questions toggle -->
          <div class="col-12 mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">Sub-questions</h6>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" :id="`has_sub_${it.uid}`" v-model="it.hasSubQuestions" @change="onRowHasSubChange(it)" />
                <label class="form-check-label" :for="`has_sub_${it.uid}`">This question has sub-questions</label>
              </div>
            </div>

            <SubQuestionsEditor v-if="it.hasSubQuestions" v-model="it.subQuestions" />
          </div>

          <div class="col-12" v-if="!it.hasSubQuestions">
            <div class="row g-2">
              <div class="col-md-4">
                <label class="form-label">Difficulty (Bloom's)</label>
                <select class="form-select form-select-sm" v-model="it.difficulty_level">
                  <option value="remembering">Remembering</option>
                  <option value="understanding">Understanding</option>
                  <option value="applying">Applying</option>
                  <option value="analyzing">Analyzing</option>
                  <option value="evaluating">Evaluating</option>
                  <option value="creating">Creating</option>
                </select>
              </div>

              <div class="col-md-3">
                <label class="form-label">Marks</label>
                <div class="input-group input-group-sm" style="max-width: 160px;">
                  <input type="number" min="0" class="form-control form-control-sm" v-model.number="it.marks" />
                  <span class="input-group-text">pts</span>
                </div>
              </div>

              <div class="col-md-5">
                <label class="form-label">Explanation (optional)</label>
                <textarea class="form-control form-control-sm" rows="2" v-model="it.explanation"></textarea>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="d-flex justify-content-between mt-3">
      <router-link class="btn btn-outline-secondary" :to="{ name: 'questions' }">Back to Questions</router-link>
      <button class="btn btn-primary" :disabled="saving || !canSave" @click="saveAll">
        {{ saving ? 'Saving...' : 'Save All' }}
      </button>
    </div>

    <div class="mt-3">
      <div class="alert alert-danger" v-if="error">{{ error }}</div>
      <div class="alert alert-success" v-if="success">Saved {{ savedCount }} questions.</div>
    </div>
  </div>
</template>

<script>
import "mathlive";
import axios from "@/axios";
import { questionService } from "@/services/questionService";
import SubQuestionsEditor from "./SubQuestionsEditor.vue";

// minimal normalize for matching pairs (avoid string/undefined)
function normalizeMatchingPairs(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export default {
  name: "BulkQuestionCreator",
  components: { SubQuestionsEditor },
  data() {
    return {
      gradeLevels: [],
      subjects: [],
      topics: [],
      selectedGrade: "",
      selectedSubject: "",
      selectedTopic: "",
      items: [],
      saving: false,
      error: "",
      success: false,
      savedCount: 0,
    };
  },
  computed: {
    canSave() {
      return !!(this.selectedGrade && this.selectedSubject && this.selectedTopic && this.items.length > 0);
    },
  },
  created() {
    this.fetchGrades();
    this.addItem(1);
  },
  methods: {
    // --- row helpers (correct_answer is ALWAYS array for non-matching) ---
    ensureRowCorrectArray(it) {
      if (Array.isArray(it.correct_answer)) return;
      if (it.correct_answer == null) it.correct_answer = [];
      else it.correct_answer = [String(it.correct_answer)];
    },
    setRowCorrectSingle(it, value) {
      this.ensureRowCorrectArray(it);
      const v = String(value ?? "");
      it.correct_answer = v ? [v] : [];
    },
    toggleRowCorrectMulti(it, value) {
      this.ensureRowCorrectArray(it);
      const v = String(value ?? "");
      if (!v) return;

      const set = new Set(it.correct_answer.map(String));
      if (set.has(v)) set.delete(v);
      else set.add(v);

      it.correct_answer = Array.from(set);
    },

    addItem(n = 1) {
      for (let i = 0; i < n; i++) {
        this.items.push({
          uid: Math.random().toString(36).slice(2),
          question_type: "mcq",
          question: "",
          options: ["", ""],

          // ✅ store as array now
          correct_answer: [],

          // for short answer we keep a separate string field but submit as [value]
          short_answer_text: "",

          explanation: "",
          difficulty_level: "remembering",
          marks: 1,
          useTextarea: false,
          is_math: false,

          // ✅ mcq multi answers toggle
          multiple_answers: false,

          optionImages: [],
          optionImagePreviews: [],
          questionImage: null,
          questionImagePreview: "",
          correctAnswerImage: null,
          correctAnswerImagePreview: "",

          matching_items: { left: ["", ""], right: ["", ""] },
          matching_pairs: [{ left_index: null, right_index: null }],

          hasSubQuestions: false,
          subQuestions: [],
        });
      }
    },
    removeItem(idx) {
      this.items.splice(idx, 1);
    },
    async fetchGrades() {
      try {
        this.gradeLevels = await questionService.fetchGradeLevels();
      } catch {
        this.gradeLevels = [];
      }
    },
    async onGradeChange() {
      this.selectedSubject = "";
      this.selectedTopic = "";
      this.topics = [];
      try {
        this.subjects = await questionService.fetchSubjects(this.selectedGrade);
      } catch {
        this.subjects = [];
      }
    },
    async onSubjectChange() {
      this.selectedTopic = "";
      try {
        this.topics = await questionService.fetchTopics(this.selectedSubject, this.selectedGrade);
      } catch {
        this.topics = [];
      }
    },

    onRowHasSubChange(it) {
      // when enabling sub-questions, parent becomes a stem/container (backend will force parent)
      if (it.hasSubQuestions) {
        // no need to erase, but you may want to clear correct answers
        it.correct_answer = [];
      }
    },

    onRowTypeChange(it) {
      // normalize fields when switching types
      if (it.question_type === "mcq") {
        if (!Array.isArray(it.options) || it.options.length < 2) it.options = ["", ""];
        if (!Array.isArray(it.correct_answer)) it.correct_answer = [];
      } else if (it.question_type === "true_false") {
        it.options = ["true", "false"];
        it.correct_answer = []; // single stored as [value]
      } else if (it.question_type === "short_answer") {
        it.options = [""];
        it.correct_answer = [];
        // keep text in short_answer_text
      } else if (it.question_type === "matching") {
        it.options = [""];
        it.correct_answer = []; // not used for matching
        if (!it.matching_items) it.matching_items = { left: ["", ""], right: ["", ""] };
        if (!Array.isArray(it.matching_pairs)) it.matching_pairs = [{ left_index: null, right_index: null }];
      }
    },

    addRowOption(it) {
      it.options.push("");
    },
    removeRowOption(it, idx) {
      const removed = it.options.splice(idx, 1)[0];

      // clear selection(s) that used removed value
      this.ensureRowCorrectArray(it);
      it.correct_answer = it.correct_answer.filter(v => v !== removed);
      if (!it.multiple_answers && it.correct_answer.length > 1) {
        it.correct_answer = [it.correct_answer[0]];
      }
    },

    buildFormData(it) {
      const fd = new FormData();

      fd.append("question_type", it.question_type);
      fd.append("question", it.question || "");

      // subject/topic/grade
      fd.append("subject_id", this.selectedSubject);
      fd.append("topic_id", this.selectedTopic);
      fd.append("grade_level_id", String(this.selectedGrade));

      // flags
      fd.append("is_math", it.is_math ? "1" : "0");
      fd.append("is_chemistry", "0");
      fd.append("is_required", "0");
      fd.append("multiple_answers", it.question_type === "mcq" ? (it.multiple_answers ? "1" : "0") : "0");

      fd.append("difficulty_level", it.difficulty_level || "remembering");
      fd.append("explanation", it.explanation || "");

      // marks: parent stem has 0
      if (it.hasSubQuestions) fd.append("marks", "0");
      else fd.append("marks", it.marks != null && it.marks !== "" ? String(it.marks) : "1");

      fd.append("has_sub_questions", it.hasSubQuestions ? "1" : "0");

      // ---- options + correct_answer ----
      if (it.hasSubQuestions) {
        // parent stem: backend will force parent; still send minimal fields
        fd.append("options[]", "");
        fd.append("correct_answer[0]", "");
      } else if (it.question_type === "mcq") {
        // normalize empty options
        it.options = (it.options || []).map((opt, idx) => {
          const v = String(opt ?? "").trim();
          return v !== "" ? v : `Option ${String.fromCharCode(65 + idx)}`;
        });

        it.options.forEach((opt, i) => {
          fd.append(`options[${i}]`, opt);
          if (it.optionImages?.[i]) fd.append(`option_images[${i}]`, it.optionImages[i]);
        });

        // correct_answer array
        this.ensureRowCorrectArray(it);
        const selected = it.correct_answer.filter(v => it.options.includes(v));
        const finalCA = it.multiple_answers ? selected : (selected.length ? [selected[0]] : []);

        if (finalCA.length === 0) fd.append("correct_answer[0]", "");
        else finalCA.forEach((val, i) => fd.append(`correct_answer[${i}]`, String(val ?? "")));

      } else if (it.question_type === "true_false") {
        fd.append("options[0]", "true");
        fd.append("options[1]", "false");

        const v = String(it.correct_answer?.[0] ?? "");
        fd.append("correct_answer[0]", v);

      } else if (it.question_type === "short_answer") {
        fd.append("options[]", "");

        const text = String(it.short_answer_text ?? "");
        fd.append("correct_answer[0]", text);

      } else if (it.question_type === "matching") {
        fd.append("options", JSON.stringify(it.matching_items || { left: ["", ""], right: ["", ""] }));
        const pairs = normalizeMatchingPairs(it.matching_pairs)
          .filter(p => p.left_index !== null && p.right_index !== null);
        fd.append("correct_answer", JSON.stringify(pairs));
      } else {
        fd.append("options[]", "");
        fd.append("correct_answer[0]", "");
      }

      // images
      if (it.questionImage) fd.append("question_image", it.questionImage);
      if (it.question_type === "short_answer" && it.correctAnswerImage) {
        fd.append("correct_answer_image", it.correctAnswerImage);
      }

      // ---- sub-questions ----
      if (it.hasSubQuestions) {
        const cleaned = (it.subQuestions || [])
          .filter(sq => (sq.question || "").toString().trim() !== "")
          .map(sq => {
            const qt = sq.question_type || "short_answer";
            const options =
              qt === "mcq"
                ? (Array.isArray(sq.options) ? sq.options.map(o => String(o ?? "")) : ["", ""])
                : qt === "true_false"
                ? ["true", "false"]
                : null;

            // correct answer array (SubQuestionsEditor already uses array)
            const caArr = Array.isArray(sq.correct_answer)
              ? sq.correct_answer.map(v => String(v ?? ""))
              : [String(sq.correct_answer ?? "")];

            let finalCA = caArr;

            // enforce single-answer for sub MCQ/TF for now
            if (qt === "mcq") {
              const sel = String(caArr[0] ?? "");
              const opts = options || ["", ""];
              finalCA = sel && opts.includes(sel) ? [sel] : (opts[0] ? [opts[0]] : []);
            } else if (qt === "true_false") {
              const sel = String(caArr[0] ?? "");
              finalCA = ["true", "false"].includes(sel) ? [sel] : [];
            } else {
              finalCA = [String(caArr[0] ?? "")];
            }

            return {
              id: sq.id ?? null,
              question: sq.question || "",
              question_type: qt,
              marks: sq.marks ?? 1,
              difficulty_level: sq.difficulty_level || "",
              options,
              correct_answer: finalCA,
            };
          });

        cleaned.forEach((sq, index) => {
          if (sq.id != null) fd.append(`sub_questions[${index}][id]`, String(sq.id));
          fd.append(`sub_questions[${index}][question]`, sq.question || "");
          fd.append(`sub_questions[${index}][question_type]`, sq.question_type);
          fd.append(`sub_questions[${index}][marks]`, String(sq.marks ?? 1));
          fd.append(`sub_questions[${index}][difficulty_level]`, sq.difficulty_level || "");

          if (sq.question_type === "mcq" || sq.question_type === "true_false") {
            (sq.options || []).forEach((opt, optIdx) => {
              fd.append(`sub_questions[${index}][options][${optIdx}]`, String(opt ?? ""));
            });
          } else {
            fd.append(`sub_questions[${index}][options]`, "");
          }

          // ✅ correct_answer array fields
          const ca = Array.isArray(sq.correct_answer) ? sq.correct_answer : [String(sq.correct_answer ?? "")];
          if (!ca.length) fd.append(`sub_questions[${index}][correct_answer][0]`, "");
          else ca.forEach((val, caIdx) => {
            fd.append(`sub_questions[${index}][correct_answer][${caIdx}]`, String(val ?? ""));
          });
        });
      }

      return fd;
    },

    onQuestionImageChange(it, e) {
      const file = e?.target?.files?.[0];
      if (!file) return;
      it.questionImage = file;
      try { it.questionImagePreview = URL.createObjectURL(file); } catch { it.questionImagePreview = ""; }
    },
    removeQuestionImage(it) {
      it.questionImage = null;
      it.questionImagePreview = "";
    },

    onCorrectAnswerImageChange(it, e) {
      const file = e?.target?.files?.[0];
      if (!file) return;
      it.correctAnswerImage = file;
      try { it.correctAnswerImagePreview = URL.createObjectURL(file); } catch { it.correctAnswerImagePreview = ""; }
    },
    removeCorrectAnswerImage(it) {
      it.correctAnswerImage = null;
      it.correctAnswerImagePreview = "";
    },

    onOptionImageChange(it, oidx, e) {
      const file = e?.target?.files?.[0];
      if (!it.optionImages) it.optionImages = [];
      if (!it.optionImagePreviews) it.optionImagePreviews = [];

      if (this.$set) this.$set(it.optionImages, oidx, file || null);
      else it.optionImages[oidx] = file || null;

      const existingUrl = it.optionImagePreviews[oidx];
      if (existingUrl && typeof URL !== "undefined") {
        try { URL.revokeObjectURL(existingUrl); } catch {}
      }

      const nextUrl = file ? URL.createObjectURL(file) : "";
      if (this.$set) this.$set(it.optionImagePreviews, oidx, nextUrl);
      else it.optionImagePreviews[oidx] = nextUrl;
    },
    clearOptionImage(it, oidx) {
      if (it.optionImages && it.optionImages.length > oidx) {
        if (this.$set) this.$set(it.optionImages, oidx, null);
        else it.optionImages[oidx] = null;
      }
      if (it.optionImagePreviews && it.optionImagePreviews.length > oidx) {
        const url = it.optionImagePreviews[oidx];
        if (url && typeof URL !== "undefined") {
          try { URL.revokeObjectURL(url); } catch {}
        }
        if (this.$set) this.$set(it.optionImagePreviews, oidx, "");
        else it.optionImagePreviews[oidx] = "";
      }
    },

    // Matching helpers
    addMatchingItem(it, column) {
      it.matching_items[column].push("");
    },
    removeMatchingItem(it, column, idx) {
      if (it.matching_items[column].length <= 2) return;
      it.matching_items[column].splice(idx, 1);

      it.matching_pairs = (it.matching_pairs || [])
        .filter(p => {
          if (column === "left" && p.left_index === idx) return false;
          if (column === "right" && p.right_index === idx) return false;
          return true;
        })
        .map(p => ({
          left_index: p.left_index !== null && p.left_index > idx ? p.left_index - 1 : p.left_index,
          right_index: p.right_index !== null && p.right_index > idx ? p.right_index - 1 : p.right_index,
        }));
    },
    addMatchingPair(it) {
      it.matching_pairs.push({ left_index: null, right_index: null });
    },
    removeMatchingPair(it, idx) {
      if (it.matching_pairs.length > 1) it.matching_pairs.splice(idx, 1);
    },

    // Math insertion helpers (unchanged)
    insertRowMathAtCursor(it, target, mode = "inline") {
      let mfRefKey = "";
      let taRefKey = "";

      if (target === "question") {
        mfRefKey = `qmf_${it.uid}`;
        taRefKey = `qta_${it.uid}`;
      } else if (target === "answer") {
        mfRefKey = `amf_${it.uid}`;
        taRefKey = `ata_${it.uid}`;
      } else {
        mfRefKey = `qmf_${it.uid}`; // reuse question math field for options
        taRefKey = `ota_${it.uid}_${target}`;
      }

      const mf = this.$refs[mfRefKey];
      const ta = this.$refs[taRefKey];
      const field = Array.isArray(mf) ? mf[0] : mf;
      const area = Array.isArray(ta) ? ta[0] : ta;
      if (!field || !area) return;

      const tex = (field.value || "").trim();
      if (!tex) return;

      const wrapped = mode === "block" ? `\n$$${tex}$$\n` : ` $${tex}$ `;

      const apply = (current) => {
        const val = String(current || "");
        const start = area.selectionStart ?? val.length;
        const end = area.selectionEnd ?? val.length;
        return val.slice(0, start) + wrapped + val.slice(end);
      };

      if (target === "question") it.question = apply(it.question);
      else if (target === "answer") it.short_answer_text = apply(it.short_answer_text);
      else if (typeof target === "number") {
        const next = apply(it.options[target]);
        this.$set ? this.$set(it.options, target, next) : it.options.splice(target, 1, next);
      }

      try { field.value = ""; } catch {}
    },

    // paste helpers (unchanged)
    onOptionsPaste(it, oidx, e) {
      try {
        const clipboard = (e.clipboardData || window.clipboardData);
        if (!clipboard) return;
        const pasted = clipboard.getData("text");
        if (!pasted) return;
        const hasNewline = /\r|\n/.test(pasted);
        const hasComma = /,/.test(pasted);
        if (!hasNewline && !hasComma) return;
        e.preventDefault();
        const items = this.normalizePastedOptions(pasted);
        if (!items.length) return;
        this.insertMultipleOptions(it, oidx, items);
      } catch {
        return;
      }
    },
    normalizePastedOptions(text) {
      const parts = text.split(/\r?\n|,/).map(s => (s || "").trim()).filter(Boolean);
      const seen = new Set();
      const out = [];
      for (const p of parts) {
        if (!seen.has(p)) { seen.add(p); out.push(p); }
      }
      return out;
    },
    insertMultipleOptions(it, oidx, newOptions) {
      if (!Array.isArray(it.options)) it.options = [];
      const first = newOptions[0];
      this.$set ? this.$set(it.options, oidx, first) : (it.options[oidx] = first);
      if (newOptions.length > 1) it.options.splice(oidx + 1, 0, ...newOptions.slice(1));
    },

    async saveAll() {
      this.error = "";
      this.success = false;
      this.savedCount = 0;

      // basic validation (updated for array correct_answer)
      for (const it of this.items) {
        if (!it.question) { this.error = "Each question must have text."; return; }

        if (it.hasSubQuestions) {
          // allow stem only; sub-questions must exist
          const anySub = (it.subQuestions || []).some(sq => (sq.question || "").toString().trim() !== "");
          if (!anySub) { this.error = "A parent question must have at least one sub-question."; return; }
          continue;
        }

        if (it.question_type === "mcq") {
          it.options = (it.options || []).map((opt, idx) => {
            const val = String(opt ?? "").trim();
            return val !== "" ? val : `Option ${String.fromCharCode(65 + idx)}`;
          });

          if (!Array.isArray(it.options) || it.options.length < 2) { this.error = "MCQ needs at least two options."; return; }

          this.ensureRowCorrectArray(it);
          const selected = it.correct_answer.filter(v => it.options.includes(v));

          if (!it.multiple_answers) {
            if (selected.length !== 1) { this.error = "MCQ needs exactly one correct answer."; return; }
          } else {
            if (selected.length < 1) { this.error = "MCQ needs at least one correct answer."; return; }
          }
        } else if (it.question_type === "true_false") {
          const v = String(it.correct_answer?.[0] ?? "");
          if (!["true", "false"].includes(v)) { this.error = "True/False must have a correct answer."; return; }
        } else if (it.question_type === "short_answer") {
          if (!String(it.short_answer_text ?? "").trim()) {  }
        } else if (it.question_type === "matching") {
          if ((it.matching_items.left || []).length < 2 || (it.matching_items.right || []).length < 2) {
            this.error = "Matching needs at least two items on each side.";
            return;
          }
        }
      }

      this.saving = true;
      try {
        const token = localStorage.getItem("auth_token");
        const headers = { "Content-Type": "multipart/form-data", ...(token && { Authorization: `Bearer ${token}` }) };

        for (const it of this.items) {
          const fd = this.buildFormData(it);
          const resp = await axios.post("/questions", fd, { headers });
          if (resp.status === 200 || resp.status === 201) this.savedCount++;
        }

        this.success = true;
      } catch (e) {
        this.error = e?.response?.data?.message || "Failed to save some questions.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.card-body label { font-weight: 600; }

.container img.img-thumbnail {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: zoom-in;
}

@media (hover: hover) and (pointer: fine) {
  .container img.img-thumbnail:hover {
    transform: scale(2);
    z-index: 10;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }
}
</style>
